import { useEffect, useId, useRef, useState } from 'react'
import type { JSX, ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { InfoTip } from './InfoTip.tsx'
import { Button } from './Form.tsx'

/**
 * ══ HỘP THOẠI THAY `alert()` / `confirm()` — NGOẠI LỆ CÓ CHỦ Ý CỦA §7 (C1, anh chốt 04/09) ══════
 *
 * Bản cũ gọi `alert()`/`confirm()` của trình duyệt **833 lần**. Quyết định C1: **đổi KHUNG, GIỮ
 * NGUYÊN ĐÚNG CÂU CHỮ CŨ** — người dùng đọc y nội dung cũ, chỉ khác cái khung. Hai lý do đủ mạnh:
 *  · hộp thoại hệ thống **không bấm được bằng Playwright** ⇒ chặn toàn bộ E2E của Task 24;
 *  · trên điện thoại nó hiện xấu và cụt, không mang được ngữ cảnh (số tiền, tên chứng từ).
 * Luật lint thứ năm (`no-restricted-syntax`, cấm `alert`/`confirm`/`prompt`) đã chặn sẵn từ Task 2.
 *
 * ⚠ ĐỢT 0 CHƯA CÓ NƠI DÙNG SẢN PHẨM — ghi rõ để không ai đi tìm ═════════════════════════════════
 * Sổ C1 nêu `/ncc/login` là "chỗ dùng thật ngay trong ĐỢT 0". Điều đó **không còn đúng**: Task 16c
 * đã giải quyết bằng khối `role="alert"` NGAY TRONG TRANG (`apps/web/src/routes/vendor-login.tsx:71,91`),
 * không dùng `alert()`. Hai component này phục vụ **D04+** (833 chỗ gọi thay dần khi chuyển từng
 * màn) và **Task 24** (E2E). KHÔNG được sửa `vendor-login.tsx` để tạo một nơi dùng giả tạo.
 *
 * ⚠ CÂU CHỮ: nhãn nút là PROP, đúng khuôn `ConfirmModal` bản cũ (`ui.tsx:958-975` — `confirmLabel`
 * và `cancelLabel` là prop BẮT BUỘC, người gọi tự cấp câu chữ). Task 19 để mặc định là chuỗi cứng
 * chép từ `i18n.ts:366`; **Task 21 nối vào kho chuỗi thật**: `common.close` = 'Đóng' ·
 * `common.agree` = 'Đồng ý' · `common.cancel` = 'Hủy' — ba khoá ĐỀU CÓ SẴN ở bản cũ, không khoá mới.
 * Nhờ vậy hộp thoại nói tiếng EN/ZH theo giao diện thay vì cứng tiếng Việt.
 * ⚠ Khoá `common.<x>` — MỘT namespace `translation` (thiết kế B), xem `src/i18n/index.ts`.
 *
 * KHÁC bản cũ ở đúng phần KHUNG (và chỉ ở đó): thêm `role="dialog"` + `aria-modal`, BẪY TIÊU ĐIỂM,
 * và TRẢ TIÊU ĐIỂM về chỗ cũ khi đóng — `ui.tsx` bản cũ không có, nên bàn phím và trình đọc màn
 * hình đi lạc ra sau lớp phủ.
 */

/** Ngăn xếp popup ĐANG MỞ — chép `ui.tsx:156` (user 01/08): phím Esc chỉ đóng popup TRÊN CÙNG,
 *  không thì mở chi tiết đơn từ trong popup bảng kê rồi bấm Esc là đóng luôn cả bảng kê phía sau. */
const modalStack: symbol[] = []

/* ══ THỨ TỰ CHỒNG THEO LÚC MỞ, KHÔNG THEO VỊ TRÍ TRONG CÂY (soát 16/10, T34) ══════════════════════
   Mọi hộp cùng `z-50`, nên hộp nào nằm SAU trong tài liệu thì vẽ đè lên. Chín tab danh mục đặt hộp
   "Bản ghi vừa bị sửa" (409) TRƯỚC hộp Sửa trong JSX ⇒ hộp xung đột mở SAU nhưng vẽ DƯỚI, nút "Tải lại"
   bị hộp Sửa che, bấm không được. Mỗi hộp nhận `z-index` = (cao nhất trong số hộp đang mở) + 1 lúc
   mở, nên hộp mở sau luôn nằm trên — khớp với `modalStack` mà phím Esc đang theo. Không đổi sang
   portal: DOM giữ nguyên chỗ, các truy vấn `container.querySelector` của test và lớp CSS theo cha
   không vỡ. Hết hộp đang mở thì đếm lại từ 50. */
const BASE_Z = 50
const openZ = new Map<symbol, number>()
const nextZ = (): number => Math.max(BASE_Z - 1, ...openZ.values()) + 1

/** Ghi hộp vào ngăn xếp Esc + bảng tầng ĐÚNG MỘT LẦN lúc mở; gỡ khi đóng. */
const useStackSlot = (): { idRef: { current: symbol | null }; z: number } => {
  const idRef = useRef<symbol | null>(null)
  // Tầng tính MỘT lần ở lần vẽ đầu (hàm khởi tạo của `useState`), không tính lại mỗi lần vẽ —
  // tính lại thì hộp nền đang gõ dở tự nhảy lên trên hộp vừa mở.
  const [z] = useState(nextZ)
  useEffect(() => {
    const me = Symbol('modal')
    idRef.current = me
    modalStack.push(me)
    openZ.set(me, z)
    return () => {
      const i = modalStack.indexOf(me)
      if (i >= 0) modalStack.splice(i, 1)
      openZ.delete(me)
    }
  }, [z])
  return { idRef, z }
}

/**
 * ══ Ô LỖI TRONG HỘP — lỗi máy chủ phải hiện TRƯỚC MẶT, không sau lớp phủ (soát 16/10, T1/T5/L18) ══
 *
 * Trước đây mọi màn đặt lỗi vào băng đỏ ĐẦU TRANG, mà băng đó nằm sau lớp phủ của hộp đang mở: người
 * dùng bấm Lưu, hộp đứng im, không thấy gì (trùng email, trùng số TK, hết phiên 401…). Bản cũ dùng
 * `alert()` ngay trước mặt, và form Đơn hàng có khung đỏ TỰ CUỘN TỚI (`Az.tsx:7258-7265`, user 10/08:
 * "bấm lưu bị chặn mà không ai thấy, tưởng đã lưu rồi đóng modal → mất số vừa nhập"). Chép đúng lớp
 * của khung đó. Cuộn tới mỗi khi CÂU LỖI ĐỔI — không cuộn mỗi lần vẽ, kẻo đang gõ thì màn giật.
 */
export const ModalError = ({ message }: { message: string }): JSX.Element => {
  const ref = useRef<HTMLDivElement>(null)
  useEffect(() => {
    const el = ref.current
    // jsdom không có `scrollIntoView` — gọi qua kiểm kiểu để test không ném.
    if (el !== null && typeof el.scrollIntoView === 'function') el.scrollIntoView({ block: 'nearest', behavior: 'smooth' })
  }, [message])
  return (
    <div ref={ref} role="alert"
         className="rounded-lg border-2 border-red-400 bg-red-50 px-3 py-2 text-xs font-bold text-red-700 whitespace-pre-line">
      {message}
    </div>
  )
}

const FOCUSABLE =
  'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])'

interface FrameProps {
  title?: string | undefined
  /** Câu chữ CHÉP NGUYÊN từ lời gọi `alert()`/`confirm()` cũ. Khung sở hữu luôn thẻ `<p>` để `id`
   *  của nó và `aria-describedby` không thể lệch nhau. */
  message: string
  /** Đóng bằng Esc / bấm nền / nút huỷ. Với `ConfirmModal` đây LÀ nhánh HUỶ (`confirm()` trả false). */
  onClose: () => void
  /** Hàng nút ở đáy. */
  children: ReactNode
  /** Lỗi máy chủ của thao tác vừa xác nhận — vẽ NGAY TRÊN hàng nút (xem `ModalError`). */
  error?: string | null | undefined
}

const ModalFrame = ({ title, message, onClose, children, error }: FrameProps): JSX.Element => {
  const boxRef = useRef<HTMLDivElement>(null)
  const { idRef, z } = useStackSlot()
  /* ⚠ `id` PHẢI duy nhất theo TỪNG HỘP THOẠI, không được là hằng số ═════════════════════════════
     Xếp chồng hộp thoại là CHỦ Ý (xem `modalStack` ở trên — Esc chỉ đóng hộp trên cùng). Với `id`
     cứng, hai hộp cùng lúc cho ra hai phần tử TRÙNG `id`; `document.getElementById` lấy phần tử
     ĐẦU TIÊN trong tài liệu ⇒ `aria-labelledby`/`aria-describedby` của hộp TRÊN trỏ về câu chữ của
     hộp DƯỚI. Trình đọc màn hình đọc sai câu rồi người dùng bấm "Đồng ý" — đúng loại lỗi mà hộp
     thoại xác nhận sinh ra để chặn. `useId()` sinh mã ổn định qua các lần render và duy nhất theo
     từng thể hiện. */
  const uid = useId()
  const labelId = `${uid}-tieu-de`
  const descId = `${uid}-noi-dung`
  /* Phóng to / thu nhỏ — mỗi hộp tự nhớ trong lúc đang mở, đúng như `Modal` bên dưới. */
  const [maximized, setMaximized] = useState(false)

  // Ngăn xếp Esc ghi ĐÚNG MỘT LẦN lúc mở (`useStackSlot`) — đăng ký lại mỗi lần render thì popup nền
  // tự nhảy lên đầu ngăn xếp và Esc lại đóng nhầm nó (bài học `ui.tsx:178-184`).

  // Tiêu điểm: nhớ chỗ đang đứng → đưa vào hộp thoại → TRẢ VỀ chỗ cũ khi đóng.
  useEffect(() => {
    const prevFocus = document.activeElement as HTMLElement | null
    boxRef.current?.querySelector<HTMLElement>(FOCUSABLE)?.focus()
    return () => { prevFocus?.focus() }
  }, [])

  useEffect(() => {
    const onKey = (e: KeyboardEvent): void => {
      if (modalStack[modalStack.length - 1] !== idRef.current) return
      if (e.key === 'Escape') {
        e.preventDefault()
        onClose()
        return
      }
      // BẪY TIÊU ĐIỂM: Tab không được rời khỏi hộp thoại ra sau lớp phủ.
      if (e.key !== 'Tab') return
      const list = Array.from(boxRef.current?.querySelectorAll<HTMLElement>(FOCUSABLE) ?? [])
      if (list.length === 0) return
      const firstEl = list[0]
      const lastEl = list[list.length - 1]
      if (!firstEl || !lastEl) return
      const activeEl = document.activeElement
      if (e.shiftKey && (activeEl === firstEl || !boxRef.current?.contains(activeEl))) {
        e.preventDefault()
        lastEl.focus()
      } else if (!e.shiftKey && activeEl === lastEl) {
        e.preventDefault()
        firstEl.focus()
      }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [onClose, idRef])

  /* ⚠ KHUNG PHẢI GIỐNG HỆT `Modal` BÊN DƯỚI, và trước đây thì KHÔNG ═══════════════════════════
     Khung này từng dựng bằng `style` thô: `<h3>`, `<textarea>`, `<button>` trần không lớp nào.
     Tailwind preflight gỡ sạch kiểu mặc định của trình duyệt cho đúng ba thẻ đó ⇒ tiêu đề hiện
     như chữ thường, ô nhập MẤT VIỀN, nút thành chữ trơ. Mà `ModalFrame` là khung của CẢ
     `AlertModal` · `ConfirmModal` · `ReasonModal` — nghĩa là mọi hộp hỏi/báo trong phần mềm đều
     hiện trần trụi so với bản cũ. Nay dùng ĐÚNG bộ lớp của `Modal`: thanh tiêu đề có ⛶ và ✕,
     thân có đệm, hàng nút ở đáy.
     ⚠ GIỮ `ModalFrame` là component RIÊNG chứ không gọi `Modal`: nó mang ba thứ `Modal` không có —
     bẫy tiêu điểm, `aria-describedby` trỏ câu chữ, và `data-testid` của nền. */
  return (
    <div className="fixed inset-0 z-50 grid place-items-center p-4" style={{ zIndex: z }}>
      {/* Bấm ra NỀN = đóng — chép `ui.tsx:216`. */}
      <div
        data-testid="modal-nen"
        aria-hidden
        onClick={onClose}
        className="absolute inset-0 bg-slate-900/40 backdrop-blur-sm"
      />
      <div
        ref={boxRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={title === undefined ? undefined : labelId}
        aria-describedby={descId}
        className={`relative z-10 flex flex-col rounded-2xl border border-slate-100 bg-white shadow-xl ${maximized
          ? 'w-[96vw] max-w-[96vw] h-[92vh] max-h-[92vh]' : 'w-full max-w-md max-h-[90vh]'}`}
      >
        <div className="flex shrink-0 items-center justify-between border-b border-slate-100 px-5 py-3.5">
          <h3 id={labelId} className="font-bold text-slate-800">{title}</h3>
          <div className="flex items-center gap-1">
            {/* ⭐ NÚT ⛶ — chú thích ngay trên đã hứa "thanh tiêu đề có ⛶ và ✕" từ lâu, nhưng khung
                này chưa bao giờ dựng nó. Rà 18/09/2026 bắt ở ba lô khác nhau: mọi `ConfirmModal` ·
                `ReasonModal` · `AlertModal` đều thiếu, tức LỆCH so với 20 hộp còn lại dùng `Modal`.
                Hộp hỏi-lý-do có ô nhập nhiều dòng nên phóng to là việc có thật, không phải trang trí. */}
            <button
              type="button"
              onClick={() => { setMaximized((m) => !m) }}
              title={maximized ? 'Thu nhỏ về cỡ mặc định' : 'Phóng to gần full màn hình'}
              className="grid h-6 w-6 place-items-center rounded text-sm leading-none text-slate-400 hover:bg-slate-100 hover:text-slate-600">
              {maximized ? '🗗' : '⛶'}
            </button>
            {/* ⚠ KHÔNG gắn `aria-label="Đóng"`: `AlertModal` đã có một nút mang đúng tên đó, hai
                phần tử cùng tên là `getByRole('button', {name})` hỏng và trình đọc màn hình đọc
                lặp. Tên của nút này lấy từ chính ký tự ×, giống hệt `Modal` bên dưới. */}
            <button type="button" onClick={onClose}
                    className="grid h-6 w-6 place-items-center rounded text-xl leading-none text-slate-400 hover:bg-slate-100 hover:text-slate-600">
              ×
            </button>
          </div>
        </div>
        <div className="min-h-0 flex-1 space-y-3 overflow-y-auto p-5">
          {/* Câu chữ giữ xuống dòng của bản cũ (`confirm()` nhiều dòng) — `whitespace-pre-line`. */}
          {message !== '' && <p id={descId} className="whitespace-pre-line text-[13px] leading-relaxed text-slate-600">{message}</p>}
          {message === '' && <p id={descId} className="sr-only">{title ?? ''}</p>}
          {error !== undefined && error !== null && error !== '' && <ModalError message={error} />}
          <div className="flex justify-end gap-2 pt-1">
            {children}
          </div>
        </div>
      </div>
    </div>
  )
}

export interface AlertModalProps {
  title?: string
  /** Câu chữ CHÉP NGUYÊN từ lời gọi `alert()` của bản cũ — không viết lại. */
  message: string
  /** Bỏ trống thì lấy `common.close` của kho chuỗi ('Đóng'). */
  closeLabel?: string
  onClose: () => void
}

/** Thay `alert()`. Một nút, đóng bằng nút / Esc / bấm nền. */
export const AlertModal = ({ title, message, closeLabel, onClose }: AlertModalProps): JSX.Element => {
  const { t } = useTranslation()
  return (
    <ModalFrame title={title} message={message} onClose={onClose}>
      <Button onClick={onClose}>{closeLabel ?? t('common.close')}</Button>
    </ModalFrame>
  )
}

export interface ConfirmModalProps {
  title?: string
  /** Câu chữ CHÉP NGUYÊN từ lời gọi `confirm()` của bản cũ — không viết lại. */
  message: string
  /** Bỏ trống thì lấy `common.agree` của kho chuỗi ('Đồng ý'). */
  confirmLabel?: string
  /** Bỏ trống thì lấy `common.cancel` của kho chuỗi ('Hủy'). */
  cancelLabel?: string
  onConfirm: () => void
  /** Nhánh HUỶ (`confirm()` trả `false`): nút Huỷ, Esc, bấm nền — cùng một lối ra, đúng bản cũ. */
  onClose: () => void
  /** Máy chủ TỪ CHỐI thao tác vừa xác nhận (xoá chặng NCC đã xác nhận, đơn đã giao…) — hộp GIỮ MỞ
   *  và câu lỗi hiện ngay trên hàng nút thay vì bị nuốt (soát 16/10, T10). */
  error?: string | null | undefined
  /** Đang chờ máy chủ: khoá nút xác nhận — nhấp đúp không gửi hai lệnh (L30). */
  busy?: boolean
}

/** Thay `confirm()`. Tên prop giữ đúng `ConfirmModal` bản cũ: `onConfirm` + `onClose`. */
export const ConfirmModal = ({
  title,
  message,
  confirmLabel,
  cancelLabel,
  onConfirm,
  onClose,
  error,
  busy = false,
}: ConfirmModalProps): JSX.Element => {
  const { t } = useTranslation()
  return (
    <ModalFrame title={title} message={message} onClose={onClose} error={error}>
      <Button variant="ghost" onClick={onClose}>{cancelLabel ?? t('common.cancel')}</Button>
      <Button disabled={busy} onClick={onConfirm}>{confirmLabel ?? t('common.agree')}</Button>
    </ModalFrame>
  )
}

export interface ReasonModalProps {
  title: string
  /** Câu giải thích dưới tiêu đề — chép nguyên văn từ lời gọi của bản cũ. */
  hint?: string
  /** Nhãn nút xác nhận. Bỏ trống thì lấy `azPurchase.reasonModal.confirm` của kho chuỗi. */
  confirmLabel?: string
  placeholder?: string
  /** Chữ điền sẵn — dùng khi ô lý do đang GIỮ một giá trị cũ cần sửa (VD cờ "báo sai hạn mức"). */
  initial?: string
  /**
   * Cho phép XÁC NHẬN VỚI Ô TRỐNG. Mặc định `false` (nút khoá khi chưa nhập) — đúng bản cũ cho ca
   * từ chối. Bật `true` cho ca mà "để trống" LÀ MỘT Ý ĐỊNH, không phải bỏ sót: bản cũ dùng nó ở
   * hộp báo sai hạn mức, nơi xoá hết chữ nghĩa là **gỡ cờ** (`AzContract.tsx:765` — `optional`).
   */
  optional?: boolean
  onOk: (reason: string) => void
  onClose: () => void
}

/**
 * Hỏi LÝ DO — chép `ReasonModal` (`proship-trans/src/pages/Az.tsx:21888`).
 *
 * Bản cũ dựng nó để thay `window.prompt`: *"prompt() bị khung xem trước VS Code chặn (trả rỗng ⇒
 * nút 'Từ chối' đứng im, người dùng tưởng app treo)"* (`Az.tsx:3217`). Giữ đúng hai điều kiện của
 * bản cũ: ô trống thì nút xác nhận KHOÁ, và lý do gửi đi đã `trim()`.
 */
export const ReasonModal = ({
  title, hint, confirmLabel, placeholder, initial, optional = false, onOk, onClose,
}: ReasonModalProps): JSX.Element => {
  const { t } = useTranslation()
  const [reason, setReason] = useState(initial ?? '')
  const trimmed = reason.trim()
  return (
    <ModalFrame title={title} message={hint ?? ''} onClose={onClose}>
      {/* `ModalFrame` xếp con vào hàng nút cuối; ô nhập phải nằm TRONG hàng đó nên cho nó chiếm
          trọn chiều ngang bằng một khối riêng. */}
      <div className="w-full">
        <textarea
          value={reason}
          onChange={(e) => { setReason(e.target.value) }}
          rows={3}
          autoFocus
          aria-label={title}
          /* ⚠ Ô "CÓ THỂ BỎ TRỐNG" PHẢI NÓI RA. Dùng chung một câu "(bắt buộc)" cho cả hai ca là
             bảo người dùng điều ngược với sự thật ở ca `optional`, nơi xoá hết chữ chính là cách
             GỠ CỜ — họ sẽ gõ bừa một chữ để nút mở ra (bản cũ `AzContract.tsx:774`). */
          placeholder={placeholder ?? t(optional
            ? 'azPurchase.reasonModal.placeholderOptional'
            : 'azPurchase.reasonModal.placeholder')}
          className="input !w-full !text-[12px]"
        />
        <div className="flex justify-end gap-2 pt-3">
          <Button variant="ghost" onClick={onClose}>{t('common.cancel')}</Button>
          <Button disabled={!optional && trimmed === ''} onClick={() => { onOk(trimmed) }}>
            {confirmLabel ?? t('azPurchase.reasonModal.confirm')}
          </Button>
        </div>
      </div>
    </ModalFrame>
  )
}

/**
 * ══ MODAL BIỂU MẪU — `ui.tsx:158-245`, bản cũ dùng 201 lần trong phạm vi RT ═════════════════════
 *
 * KHÁC HẲN ba hộp thoại phía trên: chúng thay `alert()`/`confirm()` (một câu + vài nút), còn cái
 * này là **khung chứa biểu mẫu** — Tạo/Sửa đơn, Chi tiết đơn, Phiếu thu/chi, Bảng kê…
 * Không gộp làm một: gộp thì `message: string` bắt buộc của `ModalFrame` biến mọi biểu mẫu thành
 * "một câu chữ + nội dung", và bẫy tiêu điểm của nó không hợp với form nhiều ô.
 *
 * Chép nguyên văn `className` và HÀNH VI. Bốn hành vi dễ bị bỏ sót, cái nào cũng do người dùng chốt:
 *  · **Esc đóng ĐÚNG modal trên cùng** — dùng chung `modalStack` với ba hộp thoại trên (user 01/08).
 *  · **Ctrl/Cmd+S = bấm nút Lưu chính** (user chốt 20/08: *"bấm Ctrl+S là lưu và close nhé bạn"*).
 *    Nút tự đánh dấu bằng `data-hotkey-save`; phím chỉ mô phỏng ĐÚNG MỘT cú click, không thêm hành
 *    vi nào — nút đã tự lo đóng trong `onClick` của nó.
 *  · **Phóng to / thu nhỏ** — mỗi popup tự nhớ trạng thái trong lúc đang mở.
 *  · **`dense`** nén padding cho phiếu thu/chi (user 11/08); **`noPad`** cho nội dung tự quản lý cuộn.
 *
 * ⚠ `Button` của `@rs/ui` CỐ Ý chưa có `hotkeySave` (chú thích `Form.tsx`) vì lúc đó chưa có
 *   `Modal` biểu mẫu. Nay đã có ⇒ prop đó là việc phải làm, ghi ở kế hoạch §5.2.
 */
export interface ModalProps {
  /** chuỗi hoặc JSX (vd tiêu đề kèm badge trạng thái) */
  title: ReactNode
  onClose: () => void
  children: ReactNode
  className?: string
  info?: ReactNode
  noPad?: boolean
  /** mở SẴN gần full màn hình (vd Chi tiết đơn hàng) */
  defaultMaximized?: boolean
  /** nút thao tác ở GÓC TRÊN PHẢI header (cố định, luôn với tới) — user 10/08 */
  headerActions?: ReactNode
  /** nén padding body (p-3 space-y-2) cho form dày như phiếu thu/chi — user 11/08 */
  dense?: boolean
  /** Lỗi máy chủ của thao tác trong hộp — vẽ TRONG hộp, ngay dưới thân, luôn trong tầm mắt và tự
   *  cuộn tới khi câu đổi (xem `ModalError`). Trống/`null` thì không vẽ gì. */
  error?: string | null | undefined
  /**
   * ESC HAI LẦN mới đóng — truyền vào CÂU NHẮC hiện ở lần Esc đầu. Chép form Tạo ĐNTT bản cũ
   * (`Approvals.tsx:1809-1826`, user chốt 11/07 "chống lỡ tay ESC mất dữ liệu đang nhập"): Esc lần 1
   * bị nuốt và hiện dải nhắc, lần 2 mới đóng. Bản cũ CHỈ bật cho đúng form đó. Hộp con mở chồng lên
   * (VD form Thêm NCC) vẫn ăn Esc của riêng nó vì nó nằm trên đỉnh `modalStack`. Bấm NỀN vẫn đóng
   * ngay như bản cũ (cả hai bản đều vậy — soát 16/10 T4 đã bỏ vế này).
   */
  escTwice?: string
}

export const Modal = ({
  title, onClose, children, className = '', info,
  noPad = false, defaultMaximized = false, headerActions, dense = false, error, escTwice,
}: ModalProps): JSX.Element => {
  // Ghi tên vào ngăn xếp 1 LẦN lúc mở (`useStackSlot`) — nếu đăng ký lại mỗi lần render thì popup nền
  // tự nhảy lên đầu ngăn xếp và Esc lại đóng nhầm nó.
  const { idRef: modalId, z } = useStackSlot()
  const rootRef = useRef<HTMLDivElement>(null)
  // Esc lần 1 đã bị nuốt chưa (chỉ dùng khi có `escTwice`) — bản cũ `escArmed`.
  const [escArmed, setEscArmed] = useState(false)
  useEffect(() => {
    const handler = (e: KeyboardEvent): void => {
      if (e.key !== 'Escape' || modalStack[modalStack.length - 1] !== modalId.current) return
      if (escTwice !== undefined && !escArmed) { setEscArmed(true); return }
      onClose()
    }
    window.addEventListener('keydown', handler)
    return () => { window.removeEventListener('keydown', handler) }
  }, [onClose, modalId, escTwice, escArmed])
  /* CTRL/CMD+S = bấm nút Lưu chính của phiếu (user chốt 20/08: "bấm Ctrl+S là lưu và close nhé bạn").
     CHỈ modal TRÊN CÙNG mới ăn phím (cùng cách với Esc ở trên) — tránh 2 modal lồng nhau cùng lưu.
     Không tự đóng ở đây: nút được đánh dấu data-hotkey-save đã tự lo đóng trong onClick của nó (y hệt
     khi bấm chuột), Ctrl+S chỉ mô phỏng ĐÚNG 1 cú click, không thêm hành vi nào khác. */
  useEffect(() => {
    const handler = (e: KeyboardEvent): void => {
      if (!(e.ctrlKey || e.metaKey) || e.key.toLowerCase() !== 's') return
      if (modalStack[modalStack.length - 1] !== modalId.current) return
      const btn = rootRef.current?.querySelector<HTMLButtonElement>('[data-hotkey-save]')
      if (!btn || btn.disabled) return
      e.preventDefault()
      btn.click()
    }
    window.addEventListener('keydown', handler)
    return () => { window.removeEventListener('keydown', handler) }
  }, [modalId])
  // Phóng to gần full màn hình / thu nhỏ về cỡ mặc định — mỗi popup tự nhớ trạng thái trong lúc đang mở
  const [maximized, setMaximized] = useState(defaultMaximized)
  // Id của `<h3>` để `aria-labelledby` trỏ tới — `useId` nên hai hộp thoại lồng nhau không trùng.
  const titleId = useId()
  return (
    <div className="fixed inset-0 z-50 grid place-items-center p-4" style={{ zIndex: z }}>
      <div className="absolute inset-0 bg-slate-900/40 backdrop-blur-sm" onClick={onClose} />
      {/* ⚠ `role="dialog"` + `aria-modal` nằm ở KHUNG TRONG, không ở lớp phủ: lớp phủ chứa cả nền
          mờ bấm-để-đóng, gắn vai trò lên đó là khai một hộp thoại rộng hết màn hình. Bản cũ
          (`proship-trans/src/components/ui.tsx:158`) thiếu hẳn cặp này; 16 hộp thoại tự chế của
          rs-erp thì có, nên khi gom chúng về đây phải mang theo — mất `role` là trình đọc màn hình
          hết nhận ra hộp thoại.
          ⚠ Tên hộp thoại lấy bằng `aria-labelledby` TRỎ VÀO `<h3>`, KHÔNG phải `aria-label` chép
          lại chuỗi tiêu đề: nhiều phiếu đặt cho ô nhập chính một `aria-label` TRÙNG tiêu đề (VD
          `BulkImportModals`), nên chép chuỗi là sinh ra HAI phần tử cùng nhãn — `getByLabelText`
          hỏng, và trình đọc màn hình đọc nhãn hai lần. Trỏ id thì tên vẫn đúng mà không đẻ thêm
          phần tử mang nhãn. Cách này còn đọc được cả tiêu đề dạng JSX. */}
      {/* ⚠ `min-w-0` — LỆCH CÓ CHỦ ĐÍCH so với `ui.tsx:217` bản cũ. Khung này là PHẦN TỬ LƯỚI
          (`grid place-items-center`), mặc định không co dưới bề rộng tối thiểu của nội dung: trên điện
          thoại 390 px, phiếu ĐNTT đòi ~410 px nên hộp lố mép phải và nút × bị cắt (bản cũ cũng lố,
          393 px, chỉ may còn thấy nút ×). Desktop thì dư chỗ nên dòng này không đổi gì. Soi mobile 12/10/2026. */}
      <div ref={rootRef} role="dialog" aria-modal="true" aria-labelledby={titleId}
           className={`relative z-10 min-w-0 bg-white rounded-2xl shadow-xl border border-slate-100 flex flex-col ${maximized ? 'w-[96vw] max-w-[96vw] h-[92vh] max-h-[92vh]' : `w-full ${className} max-h-[90vh]`}`}>
        <div className="flex items-center justify-between px-5 py-3.5 border-b border-slate-100 shrink-0">
          <h3 id={titleId} className="font-bold text-slate-800 flex items-center gap-1.5">
            {title}
            {info !== undefined && <InfoTip text={info} />}
          </h3>
          <div className="flex items-center gap-2">
            {headerActions !== undefined && <div className="flex items-center gap-1.5 mr-1">{headerActions}</div>}
            <button
              onClick={() => { setMaximized((m) => !m) }}
              title={maximized ? 'Thu nhỏ về cỡ mặc định' : 'Phóng to gần full màn hình'}
              className="text-slate-400 hover:text-slate-600 text-sm leading-none w-6 h-6 grid place-items-center rounded hover:bg-slate-100"
            >
              {maximized ? '🗗' : '⛶'}
            </button>
            <button onClick={onClose} className="text-slate-400 hover:text-slate-600 text-xl leading-none w-6 h-6 grid place-items-center rounded hover:bg-slate-100">
              ×
            </button>
          </div>
        </div>
        {/* Dải nhắc Esc lần 1 — lớp chép `Approvals.tsx:5466-5469`. */}
        {escTwice !== undefined && escArmed && (
          <div className="shrink-0 px-5 pt-3">
            <div role="status" className="rounded-md border border-amber-300 bg-amber-50 px-2.5 py-1 text-[11px] font-semibold text-amber-700">
              {escTwice}
            </div>
          </div>
        )}
        <div className={noPad ? 'flex-1 min-h-0 overflow-hidden' : `${dense ? 'p-3 space-y-2' : 'p-5 space-y-3'} overflow-y-auto flex-1 min-h-0`}>
          {children}
        </div>
        {/* Ô lỗi NGOÀI vùng cuộn, sát đáy hộp (ngay dưới hàng nút của thân) — thân dài cỡ nào thì
            lỗi vẫn trong tầm mắt, không phụ thuộc người gọi đặt nó ở đâu trong biểu mẫu. */}
        {error !== undefined && error !== null && error !== '' && (
          <div className={`shrink-0 ${dense ? 'px-3 pb-3' : 'px-5 pb-4'}`}><ModalError message={error} /></div>
        )}
      </div>
    </div>
  )
}
